/* SystemConfig — /admin/system-config
   Phase 4: surface governance limits stored in `system_config` so superadmins
   can tune capacity (max_total_fields, max_cases_total, …) without a deploy.
   Each row is a numeric value validated server-side; saving busts the
   governanceService cache so new limits take effect immediately. */
import { useEffect, useMemo, useState } from 'react';
import css from '../AdminPage.module.css';
import EmptyState from '../../../components/ui/EmptyState/EmptyState';
import { useSystemConfig, useUpdateSystemConfig } from '../../../hooks/useAdmin';
import { useToast } from '../../../hooks/useToast';
import type { SystemConfigEntry } from '../../../types/admin';

/** Phase 4: must mirror governanceService.DEFAULTS keys. */
const KEY_DESCRIPTIONS: Record<string, string> = {
  max_total_fields: 'Max active field definitions across the workspace',
  max_stages_per_case_type: 'Max pipeline stages per case type',
  max_cases_total: 'Soft cap on total non-archived cases',
  max_contacts_total: 'Soft cap on total contacts',
  changelog_retention_hours: 'Hours to retain field-config changelog rows',
  field_governance_warning_threshold: 'Capacity ratio (0–1) at which warnings show',
};

export default function SystemConfig() {
  const { data: rows, isLoading } = useSystemConfig();
  const update = useUpdateSystemConfig();
  const { showToast } = useToast();

  const [drafts, setDrafts] = useState<Record<string, string>>({});

  /* Seed drafts from server rows once loaded so the inputs stay controlled. */
  useEffect(() => {
    if (!rows) return;
    setDrafts((prev) => {
      const next = { ...prev };
      for (const r of rows) if (next[r.key] === undefined) next[r.key] = r.value ?? '';
      return next;
    });
  }, [rows]);

  const sortedRows = useMemo<SystemConfigEntry[]>(
    () => (rows ? [...rows].sort((a, b) => a.key.localeCompare(b.key)) : []),
    [rows],
  );

  async function save(key: string) {
    const value = drafts[key];
    if (value === undefined || value === '') {
      showToast('Value is required', 'error');
      return;
    }
    if (!/^-?\d+(\.\d+)?$/.test(value)) {
      showToast('Value must be numeric', 'error');
      return;
    }
    try {
      await update.mutateAsync({ key, value });
      showToast(`Updated ${key}`, 'success');
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } };
      showToast(e?.response?.data?.message || `Failed to update ${key}`, 'error');
    }
  }

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  return (
    <div className={css.section}>
      <div className={css.sectionHeader}>
        <h2 className={css.sectionTitle}>⚙️ System Configuration</h2>
      </div>
      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)', marginBottom: 16 }}>
        Governance limits and runtime tunables. Changes apply immediately — no deploy needed.
      </p>
      {sortedRows.length === 0 ? (
        <EmptyState message="No system_config rows" />
      ) : (
        <table className={css.table}>
          <thead>
            <tr>
              <th>Key</th>
              <th>Description</th>
              <th style={{ width: 200 }}>Value</th>
              <th style={{ width: 220 }}>Last Updated</th>
              <th style={{ width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r) => {
              const draft = drafts[r.key] ?? r.value ?? '';
              const dirty = draft !== (r.value ?? '');
              return (
                <tr key={r.key}>
                  <td style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>
                    {r.key}
                  </td>
                  <td style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)' }}>
                    {r.description || KEY_DESCRIPTIONS[r.key] || '—'}
                  </td>
                  <td>
                    <input
                      className={css.formInput}
                      value={draft}
                      onChange={(e) => setDrafts((d) => ({ ...d, [r.key]: e.target.value }))}
                    />
                  </td>
                  <td style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>
                    {r.updated_at ? new Date(r.updated_at).toLocaleString() : '—'}
                    {r.updated_by ? ` · ${r.updated_by}` : ''}
                  </td>
                  <td>
                    <button
                      className={`${css.btnPrimary} ${css.btnSmall}`}
                      onClick={() => save(r.key)}
                      disabled={!dirty || update.isPending}
                    >
                      Save
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
